import type { Product, Venue } from "@/graphql/operations";
import { useCallback, useMemo, useState } from "react";
import type { ReservationSearch } from "./reservationsTypes";

/**
 * Tracks reservation search inputs and applied search filters.
 */
export default function useReservationSearchState() {
  const [from, setFrom] = useState<Date | null>(null);
  const [until, setUntil] = useState<Date | null>(null);
  const [venueId, setVenueId] = useState<Venue["id"] | null>(null);
  const [productId, setProductId] = useState<Product["id"] | null>(null);
  const [appliedVenueId, setAppliedVenueId] = useState<Venue["id"] | null>(
    null,
  );
  const [appliedProductId, setAppliedProductId] = useState<
    Product["id"] | null
  >(null);

  const appliedSearchVariables = useMemo<ReservationSearch>(
    () => ({
      venueId: appliedVenueId ?? undefined,
      productId: appliedProductId ?? undefined,
    }),
    [appliedVenueId, appliedProductId],
  );

  const hasSearchParams = useMemo(
    () => !!(from || until || productId || venueId),
    [from, until, productId, venueId],
  );

  /**
   * Clears search input and applied filter state.
   */
  const clearSearchState = useCallback(() => {
    setFrom(null);
    setUntil(null);
    setProductId(null);
    setVenueId(null);
    setAppliedProductId(null);
    setAppliedVenueId(null);
  }, []);

  return {
    from,
    setFrom,
    until,
    setUntil,
    venueId,
    setVenueId,
    productId,
    setProductId,
    appliedVenueId,
    setAppliedVenueId,
    appliedProductId,
    setAppliedProductId,
    appliedSearchVariables,
    hasSearchParams,
    clearSearchState,
  };
}
